'use client';

import { CheckCircle } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';

const ABOUT_CONTENT = {
  badge: 'Sobre nuestra empresa',
  title: 'Conoce cómo',
  highlight: 'te ayudamos',
  buttonText: 'Leer más →',
};

const STEPS = [
  {
    id: 1,
    title: 'Elige tu próximo destino',
    description:
      'Descubre destinos increíbles en todo el mundo y encuentra el lugar ideal según tus gustos, presupuesto y planes de viaje.',
    completed: true,
  },
  {
    id: 2,
    title: 'Recibe ayuda de expertos',
    description:
      'Nuestro equipo de especialistas está aquí para ayudarte con recomendaciones personalizadas, consejos locales y todo lo necesario para crear un viaje inolvidable.',
    completed: true,
  },
  {
    id: 3,
    title: 'Reserva de forma fácil y segura',
    description:
      'Confirma tu reserva en pocos minutos y viaja con confianza. Nos encargamos de cada detalle para que tengas una experiencia sin preocupaciones.',
    completed: true,
  },
];

const GALLERY_IMAGES = [
  {
    id: 1,
    image:
      '/images/about-travel-1.jpg',
    alt: 'Destino turístico',
  },
  {
    id: 2,
    image:
      '/images/about-travel-4.jpg',
    alt: 'Vacaciones en la playa',
    offset: true,
  },
  {
    id: 3,
    image:
      '/images/about-travel-3.jpg',
    alt: 'Viajeros',
  },
  {
    id: 4,
    image:
      '/images/about-travel-2.jpg',
    alt: 'Recorrido por la ciudad',
    offset: true,
  },
];

{/* 
    const ABOUT_CONTENT = {...}
    const STEPS = [...]
    const GALLERY_IMAGES = [...]

    instead

    const { aboutContent, steps, galleryImages } = await fetchData();
*/}

export default function AboutTravelSection() {
  return (
    <section className="py-24 bg-linear-to-br from-sky-50 to-blue-100 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-start">

          {/* Left Content */}
          <div>
            <span className="text-sky-600 font-semibold uppercase tracking-wider">
              {ABOUT_CONTENT.badge}
            </span>

            <h2 className="text-5xl lg:text-6xl font-bold text-slate-900 mt-4 mb-10 leading-none">
              {ABOUT_CONTENT.title}{' '}
              <span className="text-sky-500">
                {ABOUT_CONTENT.highlight}
              </span>
            </h2>

            {/* Timeline */}
            <div className="relative pl-12">
              <div className="absolute left-5 top-5 bottom-0 w-px bg-sky-300" />

              {STEPS.map((step) => (
                <div
                  key={step.id}
                  className={`relative ${
                    step.id !== STEPS.length ? 'mb-12' : ''
                  }`}
                >
                  {/* Circle */}
                  <div className="absolute -left-12 w-10 h-10 rounded-full flex items-center justify-center shadow-lg">
                    {step.completed ? (
                      <div className="w-full h-full rounded-full bg-sky-500 flex items-center justify-center text-white">
                        <CheckCircle size={20} />
                      </div>
                    ) : (
                      <div
                        className={`w-full h-full rounded-full flex items-center justify-center text-white font-bold ${
                          step.active
                            ? 'bg-sky-500'
                            : 'bg-slate-300'
                        }`}
                      >
                        {step.id}
                      </div>
                    )}
                  </div>

                  <h3
                    className={`text-xl font-semibold mb-3 ${
                      step.active 
                        ? 'text-slate-900'
                        : 'text-slate-600'
                    }`}
                  >
                    {step.title}
                  </h3>

                  <p
                    className={`leading-relaxed ${
                      step.active
                        ? 'text-slate-800'
                        : 'text-slate-500'
                    }`}
                  >
                    {step.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-8">
              <Link href="/sobre-nosotros" className="inline-block bg-sky-500 hover:bg-sky-600 text-white px-10 py-4 rounded-xl font-semibold text-lg shadow-lg transition-all hover:-translate-y-1">
                {ABOUT_CONTENT.buttonText}
              </Link>
            </div>
          </div>

          {/* Right Gallery */}
          <div className="grid grid-cols-2 gap-5">
            {GALLERY_IMAGES.map((item) => (
              <div
                key={item.id}
                className={`overflow-hidden rounded-3xl h-70 shadow-xl group ${
                  item.offset ? 'mt-8' : ''
                }`}
              >
                <Image
                    src={item.image}
                    alt={item.alt}
                    width={600}
                    height={800}
                    className="w-full h-full object-cover group-hover:scale-110 transition duration-700"
                />
              </div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
}